import { css } from '@emotion/react';
import { ColDef, ValueFormatterParams, ValueParserParams } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { unwrap } from 'jotai/utils';
import { useCallback, useMemo } from 'react';
import { EmailArrayCellEditor } from '../../../../components/AgTable/cell-editors/EmailArrayCellEditor';
import { FieldCellEditor } from '../../../../components/AgTable/cell-editors/FieldCellEditor';
import { EditableBooleanFieldRenderer } from '../../../../components/AgTable/cell-renderers/EditableBooleanFieldRenderer';
import { StringListCellRenderer } from '../../../../components/AgTable/cell-renderers/StringListCellRenderer';
import { fyeFormField } from '../../../../schemas/Company.schema';
import {
  getKpiCollectionSettingsField,
  KpiCompanySettingsGridRow,
  kpiCompanySettingsGridRowSchema,
} from '../../../../schemas/KpiCollectionCompanySettings.schema';
import { KpiCollectionConfigurationResponse } from '../../../../schemas/KpiCollectionConfiguration.schema';
import { companyStateJ } from '../../../../services/state/CompanyStateJ';
import { portcoIdsStateJ } from '../../../../services/state/PortCosStateJ';
import { colors } from '../../../../theme/colors';
import { suppressAutocompleteKeyboardEvents } from '../../../../util/ag-grid-keyboard-utils';
import { withOverrides } from '../../../../util/ag-grid-utils';
import { getForesightStore } from '../../../../util/jotai-store';
import { useSchemaToGrid } from '../../../../util/schema-utils';
import { MONTHS } from '../../components/Sections/FormItem/KPITable/useKPITableMetricsInputs';
import {
  useKpiCollectionCompanySettingsState,
  useKpiCollectionSettingsState,
} from '../state/kpi-collection-state';

const readOnlyCellClass = 'read-only-cell';
export const KpiCompanySettingsGridStyles = css`
  &&&& .${readOnlyCellClass} {
    background: ${colors.primary[20]}18;
  }
`;
const DefaultCollectionConfig = 'All Universal KPIs';

export function useKpiCollectionCompanySettingsColDefs() {
  const schemaToGrid = useSchemaToGrid();
  const { data: kpiCollectionSettings } = useKpiCollectionSettingsState();
  const kpiSettingsById = useMemo(() => {
    return kpiCollectionSettings?.reduce((map, curr) => {
      map.set(curr.id, curr);
      return map;
    }, new Map<string, KpiCollectionConfigurationResponse>());
  }, [kpiCollectionSettings]);
  const kpiSettingsValueParser = useKpiSettingsValueParser();
  return useMemo((): ColDef[] => {
    if (!kpiCollectionSettings) {
      return [];
    }
    const schema = kpiCompanySettingsGridRowSchema(kpiCollectionSettings).omit([
      'id',
      'granularity',
      'domain',
    ]);
    const baseDefs = schemaToGrid(schema);
    const commonEditableFields: ColDef = {
      editable: true,
      cellEditor: FieldCellEditor,
      cellDataType: false,
    };
    return withOverrides(baseDefs, {
      sectorId: {
        pinned: 'left',
        width: 200,
        enableRowGroup: true,
        cellClass: [readOnlyCellClass],
        initialRowGroup: true,
        initialHide: true,
      },
      companyId: {
        pinned: 'left',
        initialSort: 'asc',
        cellClass: [readOnlyCellClass],
      },
      reporting: {
        cellRenderer: EditableBooleanFieldRenderer,
        enableRowGroup: false,
        aggFunc: (params) => params.values.length > 0 && params.values.every((v) => v === true),
      },
      kpiCollectionSettingId: {
        enableRowGroup: true,
        valueParser: kpiSettingsValueParser,
        ...commonEditableFields,
        suppressKeyboardEvent: suppressAutocompleteKeyboardEvents,
        cellEditor: FieldCellEditor,
        cellEditorParams: {
          formField: {
            ...getKpiCollectionSettingsField(kpiCollectionSettings),
            disableClearable: true,
            placeholder: DefaultCollectionConfig,
          },
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return kpiSettingsById?.get(params.value ?? '')?.name ?? DefaultCollectionConfig;
        },
        filterParams: {
          valueFormatter: (params: ValueFormatterParams) => {
            return kpiSettingsById?.get(params.value ?? '')?.name ?? DefaultCollectionConfig;
          },
        },
        comparator: (valueA, valueB) => {
          if (!kpiSettingsById) {
            return 0;
          }
          return (kpiSettingsById.get(valueA)?.name ?? DefaultCollectionConfig).localeCompare(
            kpiSettingsById.get(valueB)?.name ?? DefaultCollectionConfig,
            undefined,
            {
              sensitivity: 'base',
            }
          );
        },
      },
      fye: {
        ...commonEditableFields,
        suppressKeyboardEvent: suppressAutocompleteKeyboardEvents,
        valueParser: fyeValueParser,
        cellEditor: FieldCellEditor,
        cellEditorParams: {
          formField: fyeFormField({ key: 'fye', disableClearable: true }),
        },
        filterParams: {
          valueFormatter: (params: ValueFormatterParams) => {
            const idx = params.value ? params.value - 1 : -1;
            return MONTHS[idx] ?? undefined;
          },
        },
      },
      respondents: {
        filter: true,
        ...commonEditableFields,
        cellRenderer: StringListCellRenderer,
        cellEditorPopup: true,
        cellEditor: EmailArrayCellEditor,
        suppressKeyboardEvent: suppressAutocompleteKeyboardEvents,
        cellEditorParams: {
          fieldPath: 'respondents',
          extraValidator: respondentsValidator,
        },
        valueParser: stringArrayValueParser,
        minWidth: 200,
      },
    });
  }, [kpiCollectionSettings, schemaToGrid, kpiSettingsValueParser, kpiSettingsById]);
}

export function useKpiCompanySettingsGridData(): KpiCompanySettingsGridRow[] {
  const { data: kpiCollectionCompanySettings } = useKpiCollectionCompanySettingsState();
  const portcoIds = useAtomValue(portcoIdsStateJ);
  const settingsByCompanyId = useMemo(() => {
    return kpiCollectionCompanySettings?.reduce((acc, setting) => {
      acc.set(setting.companyId, setting);
      return acc;
    }, new Map<number, (typeof kpiCollectionCompanySettings)[number]>());
  }, [kpiCollectionCompanySettings]);

  return useMemo(() => {
    if (!kpiCollectionCompanySettings) {
      return [];
    }
    if (!portcoIds) {
      return [];
    }
    const store = getForesightStore();
    return portcoIds.map((id) => {
      const company = store.get(unwrap(companyStateJ(id)));
      const setting = settingsByCompanyId?.get(id);
      return {
        fye: company?.fye,
        kpiCollectionSettingId: setting?.kpiCollectionSettingId,
        companyId: id,
        reporting: company?.reporting,
        respondents: company?.respondents as string[] | null,
        sectorId: company?.sectorId,
        domain: company?.domain,
      };
    });
  }, [kpiCollectionCompanySettings, portcoIds, settingsByCompanyId]);
}

function useKpiSettingsValueParser() {
  const kpiCollectionSettings = useKpiCollectionSettingsState();
  const settingsByName = useMemo(() => {
    return kpiCollectionSettings.data?.reduce((map, curr) => {
      map.set(curr.name, curr);
      return map;
    }, new Map<string, KpiCollectionConfigurationResponse>());
  }, [kpiCollectionSettings]);
  return useCallback(
    (params: ValueParserParams) => {
      return settingsByName?.get(params.newValue ?? '')?.id ?? null;
    },
    [settingsByName]
  );
}

function fyeValueParser(params: ValueParserParams) {
  const value = params.newValue;
  if (!value) {
    return params.oldValue;
  }

  // Handle numeric strings from fill operations (e.g., "12" for December)
  const numericValue = Number(value);
  if (!isNaN(numericValue) && numericValue >= 1 && numericValue <= 12) {
    return numericValue;
  }

  // Handle month name strings from cell editor (e.g., "December")
  const idx = MONTHS.indexOf(value ?? '');
  if (idx === -1) {
    return params.oldValue;
  }
  return idx + 1;
}

export function stringArrayValueParser(params: ValueParserParams) {
  if (!params.newValue) {
    return [];
  }
  const values = params.newValue
    .split(',')
    .map((v: string) => v.trim())
    .filter((v: string) => v !== '');
  return values;
}

export const defaultKpiCompanySettingsColDef: ColDef<KpiCompanySettingsGridRow> = {
  flex: 1,
  minWidth: 150,
  sortable: true,
  filter: true,
  filterParams: {
    buttons: ['reset'],
  },
};

export const autoGroupColumnDef: ColDef = {
  filter: 'agGroupColumnFilter',
  pinned: 'left',
};

export function respondentsValidator(
  data?: Pick<KpiCompanySettingsGridRow, 'domain'>,
  currentValue?: string[] | null
): string | null {
  const companyDomain = data?.domain?.trim().toLowerCase();
  if (!companyDomain || !currentValue?.length) {
    return null;
  }

  const hasMismatch = currentValue.some((email) => {
    const domain = email.trim().split('@')[1]?.trim().toLowerCase();
    return domain && domain !== companyDomain;
  });

  return hasMismatch ? "Some emails don't match the company domain" : null;
}
